'use client';

import { BadgeCheck, BarChart3, Calendar, Check, Clock, ExternalLink, Star, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';
import { useToday } from '@/shared/hooks/useToday';

import { CourseCardCartIconButton } from './CourseCardCartIconButton';
import { CourseCardDiscountBadge } from './CourseCardDiscountBadge';
import type { CourseCardCartState, CourseCardItem } from '../types/course-card';
import {
  formatCourseCardCount,
  formatCourseCardCurrency,
  formatCourseCardModality,
  formatCourseCardRating,
  formatCourseCardStartDate,
  getCourseCardTeachers,
  getCourseCardPriceParts,
} from '../lib/course-card-formatters';
import { hasHtmlMarkup, sanitizeCatalogHtml } from '../lib/course-card-html';
import { resolveCourseCardHref } from '../lib/course-card-links';

interface CourseCardPopoverProps {
  course: CourseCardItem;
  cartState: CourseCardCartState;
  onCartAction: () => void;
  onCartFlashAnimationComplete?: () => void;
  onNavigate?: (event: MouseEvent<HTMLAnchorElement>) => void;
  onClose?: () => void;
  showCloseButton?: boolean;
  hidePrice?: boolean;
}

interface MetaChipProps {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}

const TEACHERS_PREVIEW_LIMIT = 2;

const MetaChip = ({ children, icon, className }: MetaChipProps) => (
  <span
    className={cn(
      'border-(--color-border-subtle) text-(--text-secondary) inline-flex min-h-8 items-center gap-1.5 rounded-lg border bg-white px-3 text-xs font-medium shadow-[0_1px_2px_rgba(19,1,63,0.03)]',
      className,
    )}
  >
    {icon}
    {children}
  </span>
);

const CourseTeachersList = ({ course }: { course: CourseCardItem }) => {
  const [visibleTeachersCount, setVisibleTeachersCount] = useState(TEACHERS_PREVIEW_LIMIT);
  const teachers = getCourseCardTeachers(course);

  useEffect(() => {
    setVisibleTeachersCount(TEACHERS_PREVIEW_LIMIT);
  }, [course.id]);

  if (teachers.length === 0) return null;

  const hasMoreTeachers = visibleTeachersCount < teachers.length;
  const hasLessTeachers = visibleTeachersCount > TEACHERS_PREVIEW_LIMIT;
  const visibleTeachers = teachers.slice(0, visibleTeachersCount);
  const previewTeacher = hasMoreTeachers ? teachers[visibleTeachersCount] : null;

  const showMoreTeachers = () => {
    setVisibleTeachersCount((currentCount) =>
      Math.min(currentCount + TEACHERS_PREVIEW_LIMIT, teachers.length),
    );
  };

  const showLessTeachers = () => {
    setVisibleTeachersCount((currentCount) =>
      Math.max(currentCount - TEACHERS_PREVIEW_LIMIT, TEACHERS_PREVIEW_LIMIT),
    );
  };

  return (
    <div className="space-y-2">
      <p className="text-muted-text text-[0.68rem] font-bold uppercase tracking-[0.08em]">
        Docentes
      </p>
      <ul className="space-y-2">
        {visibleTeachers.map((teacher) => (
          <li
            className="bg-(--color-background-canvas) flex items-center gap-2.5 rounded-lg p-2.5"
            key={teacher.id ?? teacher.name}
          >
            {teacher.imageUrl ? (
              <span className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full">
                <Image
                  fill
                  alt={teacher.name}
                  className="object-cover"
                  sizes="32px"
                  src={teacher.imageUrl}
                />
              </span>
            ) : null}
            <span className="min-w-0">
              <span className="text-navy block text-[0.78rem] font-semibold leading-snug">
                {teacher.name}
              </span>
            </span>
          </li>
        ))}
        {previewTeacher ? (
          <li
            aria-hidden="true"
            className="pointer-events-none h-7 overflow-hidden rounded-lg opacity-70 blur-[0.4px] [mask-image:linear-gradient(to_bottom,black_0%,black_35%,transparent_100%)]"
            key={`preview-${previewTeacher.id ?? previewTeacher.name}`}
          >
            <div className="bg-(--color-background-canvas) flex items-center gap-2.5 rounded-lg p-2.5">
              {previewTeacher.imageUrl ? (
                <span className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full">
                  <Image
                    fill
                    alt=""
                    className="object-cover"
                    sizes="32px"
                    src={previewTeacher.imageUrl}
                  />
                </span>
              ) : null}
              <span className="min-w-0">
                <span className="text-navy block text-[0.78rem] font-semibold leading-snug">
                  {previewTeacher.name}
                </span>
              </span>
            </div>
          </li>
        ) : null}
      </ul>
      {hasMoreTeachers || hasLessTeachers ? (
        <div className="flex flex-wrap gap-3">
          {hasMoreTeachers ? (
            <button
              className="text-(--color-primary-600) hover:text-(--color-primary-700) focus-visible:ring-(--color-primary-500) cursor-pointer text-[0.78rem] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              type="button"
              onClick={showMoreTeachers}
            >
              Ver más
            </button>
          ) : null}
          {hasLessTeachers ? (
            <button
              className="text-(--color-primary-600) hover:text-(--color-primary-700) focus-visible:ring-(--color-primary-500) cursor-pointer text-[0.78rem] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              type="button"
              onClick={showLessTeachers}
            >
              Ver menos
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export function CourseCardPopover({
  course,
  cartState,
  onCartAction,
  onCartFlashAnimationComplete,
  onNavigate,
  onClose,
  showCloseButton = false,
  hidePrice = false,
}: CourseCardPopoverProps) {
  const href = resolveCourseCardHref(course);
  const today = useToday();
  const startLabel = formatCourseCardStartDate(
    course.startDate,
    course.modality,
    course.currency,
    today,
  );
  const modality = formatCourseCardModality(course.modality);
  const hasRating = course.rating > 0;
  const highlights = course.highlights?.slice(0, 3) ?? [];
  const hasDiscount = Boolean(course.originalPrice && course.originalPrice > course.price);
  const formattedPrice = formatCourseCardCurrency(course.price, course.currency);
  const priceParts = getCourseCardPriceParts(course.price, course.currency);
  const description = course.description?.trim();
  const descriptionHtml =
    description && hasHtmlMarkup(description) ? sanitizeCatalogHtml(description) : null;

  // ROLLBACK TEMPORAL: el footer (precio "Precio actual" + botones en el drawer
  // mobile) se ocultó porque replicaba información que ya muestra la card padre,
  // restándole sentido al popover. El código se conserva intacto abajo; para
  // reactivarlo basta poner este flag en true.
  const SHOW_POPOVER_FOOTER: boolean = false;

  return (
    <div className="adipa-course-card-popover max-w-124 border-(--color-border-subtle) lg:w-124 flex max-h-[min(calc(100svh-11rem),42rem)] w-full flex-col overflow-hidden rounded-2xl border bg-white shadow-[0_18px_46px_rgba(19,1,63,0.16)]">
      <div className="min-h-0 overflow-y-auto px-5 pb-4 pt-5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="space-y-3">
              <h4 className="text-navy text-[1.25rem] font-bold leading-[1.22]">{course.title}</h4>

              <CourseTeachersList course={course} />
            </div>
          </div>

          {showCloseButton ? (
            <button
              aria-label="Cerrar detalle rápido"
              className="text-muted-text hover:bg-(--color-muted) hover:text-navy focus-visible:ring-(--color-primary-500) inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              type="button"
              onClick={onClose}
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {course.allianceLabel ? (
            <MetaChip
              className="border-transparent bg-(--course-alliance) font-semibold text-white"
              icon={<BadgeCheck aria-hidden="true" className="h-3.5 w-3.5" />}
            >
              Doble certificación con {course.allianceLabel}
            </MetaChip>
          ) : null}
          {hasRating ? (
            <MetaChip
              icon={
                <Star
                  aria-hidden="true"
                  className="fill-(--course-rating-star) text-(--course-rating-star) h-3.5 w-3.5"
                />
              }
            >
              {formatCourseCardRating(course.rating)} (
              {formatCourseCardCount(course.studentsCount)})
            </MetaChip>
          ) : null}
          <MetaChip
            icon={
              <span
                aria-hidden="true"
                className={cn(
                  'relative h-2 w-2 rounded-full',
                  course.modality === 'en-vivo' ? 'bg-(--course-live)' : 'bg-(--color-primary-500)',
                )}
              >
                {course.modality === 'en-vivo' ? (
                  <span className="bg-(--course-live-soft) absolute inset-[-0.18rem] animate-ping rounded-full motion-reduce:animate-none" />
                ) : null}
              </span>
            }
          >
            {modality}
          </MetaChip>
          {course.hours ? (
            <MetaChip icon={<Clock aria-hidden="true" className="h-3.5 w-3.5" />}>
              {course.hours} hrs
            </MetaChip>
          ) : null}
          {course.level ? (
            <MetaChip icon={<BarChart3 aria-hidden="true" className="h-3.5 w-3.5" />}>
              {course.level}
            </MetaChip>
          ) : null}
        </div>

        {startLabel && course.canPurchase !== false ? (
          <p className="text-(--text-secondary) mt-4 inline-flex items-center gap-2 text-[0.78rem] font-medium">
            <Calendar aria-hidden="true" className="text-muted-text h-4 w-4" />
            {startLabel}
          </p>
        ) : null}

        {course.statusLabel ? (
          <p className="bg-(--surface-accent-soft) text-(--color-primary-700) mt-3 flex w-fit items-center gap-2 rounded-lg px-3 py-1.5 text-[0.74rem] font-semibold">
            <span
              aria-hidden="true"
              className="bg-(--color-primary-500) h-1.5 w-1.5 shrink-0 rounded-full"
            />
            {course.statusLabel}
          </p>
        ) : null}

        {description ? (
          <section className="mt-5 space-y-2" aria-labelledby={`course-description-${course.id}`}>
            <h5
              className="text-muted-text text-[0.68rem] font-bold uppercase tracking-[0.08em]"
              id={`course-description-${course.id}`}
            >
              Descripción
            </h5>
            {descriptionHtml ? (
              <div
                className="text-(--text-secondary) line-clamp-3 text-[0.8rem] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            ) : (
              <p className="text-(--text-secondary) line-clamp-3 text-[0.8rem] leading-relaxed">
                {description}
              </p>
            )}
          </section>
        ) : null}

        {highlights.length > 0 ? (
          <ul className="mt-5 space-y-2">
            {highlights.map((highlight) => (
              <li
                className="text-(--text-secondary) flex items-start gap-2.5 text-[0.8rem] leading-snug"
                key={highlight}
              >
                <Check
                  aria-hidden="true"
                  className="text-(--color-primary-500) mt-0.5 h-4 w-4 shrink-0"
                  strokeWidth={2.7}
                />
                <span className="line-clamp-3">{highlight}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {SHOW_POPOVER_FOOTER ? (
        <div className="border-(--color-border-subtle) shrink-0 space-y-3 border-t bg-white px-5 pb-5 pt-3 shadow-[0_-12px_24px_rgba(255,255,255,0.94)]">
          {!hidePrice ? (
            <div className="space-y-1.5">
              <p className="text-muted-text text-[0.68rem] font-bold">Precio actual</p>
              <div className="grid grid-cols-[minmax(0,auto)_auto] items-start justify-start gap-x-3 gap-y-1">
                <span
                  aria-label={formattedPrice}
                  className="text-navy inline-flex items-baseline gap-1.5 leading-none tracking-tight"
                >
                  <span className="text-[0.78rem] font-medium">{priceParts.currencyLabel}</span>
                  <span className="text-[1.35rem] font-bold">{priceParts.amountLabel}</span>
                </span>
                {course.discount ? (
                  <CourseCardDiscountBadge discount={course.discount} />
                ) : null}
                {hasDiscount ? (
                  <span className="text-muted-text col-start-1 text-[0.75rem] font-semibold line-through">
                    {formatCourseCardCurrency(course.originalPrice ?? course.price, course.currency)}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {showCloseButton ? (
            <div className="flex gap-2">
              <Link
                className="border-(--color-primary-500) text-(--color-primary-600) hover:bg-(--color-primary-500) hover:border-(--color-primary-500) hover:text-adipa-primary-foreground focus-visible:ring-(--color-primary-500) inline-flex h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border bg-white px-3 text-[0.82rem] font-semibold transition-[background-color,border-color,color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-[0_10px_22px_rgba(112,78,253,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
                href={href}
                onClick={onNavigate}
              >
                <ExternalLink
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0"
                  strokeWidth={2.1}
                />
                Ver detalle
              </Link>
              {course.canPurchase !== false ? (
                <CourseCardCartIconButton
                  aria-label={cartState === 'pay' ? 'Ir a pagar' : 'Agregar al carrito'}
                  cartState={cartState}
                  onAddingAnimationComplete={onCartFlashAnimationComplete}
                  onClick={(event) => {
                    event.stopPropagation();
                    onCartAction();
                  }}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
